import { Item, OptimizationLogEntry } from "@/api"
import SeparablePanel from "@/components/SeparablePanel"
import { Checkbox } from "primereact/checkbox"
import { Column } from "primereact/column"
import { Message } from "primereact/message"
import { useMemo, useState } from "react"
import { extractToNumbersFromNumberWithBold, NumberWithBold } from "../types"
import InventoryChart from "./charts/InventoryChart"
import ItemMasterDetail from "./ItemMasterDetail"
import ItemProductionDataTable from "./ItemProductionDataTable"
import { ItemStats } from "./ItemTreeTable"
import OptimizationLogsPanel from "./OptimizationLogsPanel"

interface ItemDetailProps {
  items: Item[],
  dates: string[]
  itemStats: Record<Item["品目ID"], ItemStats>
  inventories: Record<Item["品目ID"], number[]>
  shipments: Record<Item["品目ID"], number[]>
  productions: Record<Item["品目ID"], NumberWithBold[]>;
  onProductionChange?: (itemId: string, dateIndex: number, value: number, isBold: boolean) => void;
  // 各データソースを区別するための追加データ
  excelProductions?: Record<Item["品目ID"], NumberWithBold[]>; // Excel由来の生産数
  optimizerProductions?: Record<Item["品目ID"], NumberWithBold[]>; // Optimizer由来の生産数
  userProductions?: Record<Item["品目ID"], Record<number, NumberWithBold>>; // ユーザー入力の生産数
  logs?: OptimizationLogEntry[]
}

const ItemDetail = ({
  items,
  itemStats,
  inventories,
  shipments,
  productions,
  dates,
  onProductionChange,
  excelProductions,
  optimizerProductions,
  userProductions,
  logs,
}: ItemDetailProps) => {
  const [selectedItems, setSelectedItems] = useState<Item[]>([])
  const [compare, setCompare] = useState(false)
  const filteredSelectedItems = useMemo(() => {
    return selectedItems.length > 0 ? selectedItems : items
  }, [selectedItems, items])
  const filteredSelectedItemSummary = useMemo(() => {
    if (filteredSelectedItems.length === 0) return []
    const summary = {
      '出荷数合計': filteredSelectedItems.reduce((sum, item) => sum + itemStats[item.品目ID].総出荷, 0),
      '生産数合計': filteredSelectedItems.reduce((sum, item) => sum + itemStats[item.品目ID].総生産, 0),
      // '生産回数（のべ）': filteredSelectedItems.map(item => item.生産日).flat().length,
      // '生産日': [...new Set(filteredSelectedItems.map(item => item.生産日).flat())].length,
      // '工程時間合計': filteredSelectedItems.reduce((sum, item) => sum + item.工程時間, 0),
    }
    return Object.entries(summary).map(([label, value]) => ({ label, value }))
  }, [filteredSelectedItems, itemStats])


  const title = filteredSelectedItems.length > 0 ? (
    filteredSelectedItems.length > 1 ? `選択中品目(${filteredSelectedItems.length})の詳細` : `${filteredSelectedItems[0].品目ID}:${filteredSelectedItems[0].品目名}の詳細`
  ) : "選択されていません"
  return (
    <div className="flex flex-column gap-2">
      <h3>{title}</h3>
      <div className="py-2">
        <ItemMasterDetail
          items={filteredSelectedItems}
          itemStats={itemStats}
          onSelectionChange={setSelectedItems}
        />
      </div>
      <div className="py-2">
        <label className="flex flex-row gap-2 line-height-2">
          <Checkbox
          value={true}
          checked={compare}
          onChange={(e) => {
            console.log("compare", e.checked)
            setCompare(e.checked ?? false)
          }}
          />
          <span>Excel上の計画と比較する</span>
        </label>
      </div>
    <SeparablePanel toggleable header="在庫推移予測">
        {filteredSelectedItems.length > 10 ? (
          <Message severity="warn" text="選択された品目が多いため、グラフ表示を制限しています" />
        ) : (
          <InventoryChart
            items={filteredSelectedItems}
            日付={dates}
            inventories={inventories}
            shipments={shipments}
            productions={extractToNumbersFromNumberWithBold(optimizerProductions || {})}
            compare={compare}
          />
        )}
    </SeparablePanel>
    <SeparablePanel toggleable header="在庫推移詳細">
      <ItemProductionDataTable
      items={filteredSelectedItems}
      日付={dates}
      inventories={inventories}
      shipments={shipments}
      productions={productions}
      compare={compare}
      onProductionChange={onProductionChange}
      excelProductions={excelProductions}
      optimizerProductions={optimizerProductions}
      userProductions={userProductions}
      />
    </SeparablePanel>
    {logs && logs.length > 0 && (
      <SeparablePanel toggleable collapsed header="最適化ログ">
        <OptimizationLogsPanel
          logs={logs}
          defaultFilter={filteredSelectedItems.length === 1 ? `id:${filteredSelectedItems[0].品目ID}` : ''}
        />
      </SeparablePanel>
    )}
    </div>
  )
}

export default ItemDetail
